import { Bot as GrtsBot } from 'grts-utils/src/types/Bot';
import { Exchange } from '../../types/exchange';
import { Transaction } from '../Overview/types';
import {
  CbrtRangeState,
  CurrentProfit,
  MrcState as CalculatedMrcState,
  Transaction as ProfitTransaction,
  IndicatorDataPoint,
  IndicatorsConfig,
} from '../ProfitCalculations/types';

export type BotTimeFrames = {
  higher: TimeFrame;
  shorter: TimeFrame;
  multiOrder: TimeFrame;
};

export type BotComplexParams = {
  cbrt: number | null;
  maxDecimals: number;
  maxMultiOrderAmount: number;
  maxOrderSlippage: number;
  shorterTimeFrameInterval: number;
  startBalance: number;
  supportCbrtRange: boolean;
  supportMrcState: boolean;
  secondHighestProfitTimeFrame: string;
  thirdHighestProfitTimeFrame: string;
};

export type Bot = {
  timeFrame: BotTimeFrames;
  baseAsset: string;
  botId: string;
  isActive: boolean;
  isEnabled: boolean;
  quoteAsset: string;
  runtime?: {
    cbrtRange?: any;
    didEarlierOrder?: {
      value: boolean;
      timestamp: number;
    };
    latestOrder?: string;
    latestTransactionType?: string;
    multiOrder?: { position: number };
    retryUpdateComplexParams?: boolean | null;
    mrcState?: MrcState;
  };
  complexParams?: BotComplexParams;
  currentProfit: number;
  dateCreated?: string;
  defaultExchange: Exchange;
  activePort?: number;
  lastUpdated: number;
  availableExchanges: Exchange[];
};

export type Candle = {
  id?: number;
  startDate: string;
  startTimestamp: number;
  endDate: string;
  endTimestamp: number;
  open: number;
  close: number;
  low: number;
  high: number;
  direction: string;
  startOfNewTrend: boolean | null;
  decision: boolean | null;
  didMakeDecision: boolean | null;
};

export enum CandleType {
  Normal = 'NORMAL',
  Heikin = 'HEIKIN',
}

export type DataPoint = {
  x: number;
  open: number;
  close: number;
  low: number;
  high: number;
};

export enum TimeFrame {
  OneYear = '1Y',
  OneQuarter = '1Q',
  TwoMonths = '2M',
  OneMonth = '1M',
  ThreeWeeks = '3w',
  TwoWeeks = '2w',
  OneAndHalfWeek = '1.5w',
  OneWeek = '1w',
  HalfWeek = '0.5w',
  TenDays = '10d',
  SixDays = '6d',
  FiveDays = '5d',
  FourDays = '4d',
  ThreeDays = '3d',
  TwoDays = '2d',
  OneDay = '1d',
  HundredHours = '100h',
  TwentyHours = '20h',
  EighteenHours = '18h',
  FifteenHours = '15h',
  TwelveHours = '12h',
  TenHours = '10h',
  EightHours = '8h',
  SixHours = '6h',
  FiveHours = '5h',
  FourHours = '4h',
  ThreeHours = '3h',
  TwoHours = '2h',
  OneHour = '1h',
  HundredMinutes = '100m',
  SeventyFiveMinutes = '75m',
  FiftyMinutes = '50m',
  FortyMinutes = '40m',
  ThirtyMinutes = '30m',
  TwentyMinutes = '20m',
  FifteenMinutes = '15m',
  TenMinutes = '10m',
  EightMinutes = '8m',
  SixMinutes = '6m',
  FiveMinutes = '5m',
  FourMinutes = '4m',
  ThreeMinutes = '3m',
  TwoMinutes = '2m',
  OneMinute = '1m',
  // New for TPTs
  SixtyHours = '60h', // 2.5 days
  FortySixHours = '46h',
  FortyFourHours = '44h',
  FortyTwoHours = '42h',
  FortyHours = '40h',
  ThirtyEightHours = '38h',
  ThirtySixHours = '36h',
  TwentyThreeHours = '23h',
  TwentyTwoHours = '22h',
  TwentyOneHours = '21h',
  NineTeenHours = '19h',
}

export enum Scale {
  Linear = 'linear',
  Logarithmic = 'log',
}

export enum Tabs {
  Charts = 'Charts',
  Transactions = 'Transactions',
  Control = 'Control',
  ExtraInfo = 'Extra Info',
}

export interface TransactionDetailsApiResponse {
  allTransactions: Transaction[];
  compactTransactions: Transaction[];
  currentProfit: CurrentProfit | undefined;
}

export interface ProfitApiResponse {
  total: {
    stable: number;
    asset: number;
  };
  current: {
    stable: number;
    asset: number;
  };
  noAlgorithmicProgressionProfit: number;
}

export type SubscriptionBalance = {
  stable: {
    start: number | null;
    reserve: number;
    current: number;
    shortCode: string | null;
  };
  asset: {
    start: number | null;
    reserve: number;
    current: number;
    shortCode: string | null;
  };
};

export type Subscription = {
  id: number;
  botId: string;
  clusterId: string;
  userId: number;
  email: string | null | undefined;
  isActive: boolean;
  isAutomatedOrder: boolean;
  isNotificationEnabled: boolean;
  fee: {
    latest: number;
    total: number;
  };
  totalValue: number;
  multiOrder?: {
    volume: number;
    initialVolume: number;
  };
};

export type CbrtRange = {
  isActive: boolean;
  open: number;
  direction: string;
  timestamp: number;
};

export type MrcState = {
  isActive: boolean;
  direction: string;
  startTimestamp: number;
  lastUpdated: number;
  state: string;
  orderType: string;
  transactionType: string;
  timeFrame: string;
  rejectedPrice: number;
};

export type ComplexParamsBundle = {
  complexParams: Partial<BotComplexParams>;
  timeFrames: BotTimeFrames;
};

export type LatestBotState = {
  orderType: string;
  currentProfit: number;
  currentPriceChange: number;
  candles: Candle[];
  wakeUpTimes: number[];
  cbrtRangeStates: CbrtRangeState[];
  mrcStates: CalculatedMrcState[];
  transactions: ProfitTransaction[];
  bot?: GrtsBot;
  baseAsset: string;
  quoteAsset: string;
  timeFrame: TimeFrame;
  exchange: Exchange;
  startTimestamp?: number;
  endTimestamp?: number;
  signals?: IndicatorDataPoint[];
  indicatorConfig?: IndicatorsConfig;
};
